import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const BASE = "/admin/campus/";

export interface Supplier {
	id: number;
	name: string;
	countryCode: string;
	currencyId: number | null;
	contactName: string | null;
	phone: string | null;
	email: string | null;
	taxId: string | null;
	paymentTerms: string | null;
	leadTimeDays: number | null;
	notes: string | null;
	active: boolean;
	orders: number;
}

export interface OrderSummary {
	id: number;
	folio: string;
	statusId: number;
	status: string;
	supplierId: number;
	supplierName: string | null;
	warehouseId: number;
	warehouseName: string | null;
	currencyId: number;
	currencyCode: string | null;
	currencySymbol: string;
	localCurrencyCode: string | null;
	localCurrencySymbol: string;
	exchangeRate: number;
	expectedAt: string | null;
	supplierReference: string | null;
	lineCount: number;
	units: number;
	received: number;
	pending: number;
	subtotal: number;
	costsTotal: number;
	totalLocal: number;
	paid: number;
	balance: number;
	/** Pagado de más (por cancelaciones): se puede pasar a otra orden del proveedor. */
	supplierCredit: number;
	/** Dinero eximido por piezas canceladas. */
	cancelledAmount: number;
	nextDue?: string | null;
	nextDueOverdue?: boolean;
	credit: boolean;
	late: boolean;
	costsPendingDays: number | null;
	costsFlag: "warning" | "alert" | null;
	createdBy: string;
	createdAt: string | null;
	approvedBy: string | null;
	approvedAt: string | null;
	closeReason: string | null;
}

export interface OrderLine {
	id: number;
	articleId: number;
	name: string;
	waCode: string | null;
	barcode: string | null;
	sizeValue: string | null;
	unit: string | null;
	quantity: number;
	unitCost: number;
	subtotal: number;
	received: number;
	damaged: number;
	cancelled: number;
	pending: number;
	landedUnitCost: number | null;
	notes: string | null;
}

export interface OrderFile { id: number; name: string; kind: string }

export interface Installment { id: number; dueDateIso: string; dueDate: string; amount: number; paid: number; pending: number; note: string | null; overdue: boolean; daysLeft: number }

export interface Payable extends Installment { orderId: number; supplierId: number; folio: string; statusId: number; status: string; supplierName: string | null; currencyCode: string | null; currencySymbol: string }

export interface PendingLine { orderId: number; folio: string; supplierName: string | null; warehouseName: string | null; articleId: number; name: string; waCode: string | null; ordered: number; received: number; damaged: number; pending: number; expectedAt: string | null; late: boolean; partial: boolean }

export interface OrderDetail extends OrderSummary {
	supplier: Supplier | null;
	notes: string | null;
	lines: OrderLine[];
	costs: { id: number; type: string; typeName: string; amount: number; reference: string | null; vendor: string | null; shared: boolean; note: string | null; author: string; createdAt: string | null; voided: boolean; voidReason: string | null; files: OrderFile[] }[];
	installments: Installment[];
	payments: { id: number; amount: number; exchangeRate: number; paidAt: string; reference: string | null; note: string | null; author: string; files: OrderFile[]; kind: string; paymentTotal: number; otherOrders: string[] }[];
	cancellations: { name: string; quantity: number; amount: number; reason: string; author: string; createdAt: string | null }[];
	receipts: { id: number; createdAt: string | null; author: string; note: string | null; lines: { name: string; quantity: number; damaged: number }[]; files: OrderFile[] }[];
	files: OrderFile[];
	history: { type: string; summary: string; note: string | null; author: string; createdAt: string | null }[];
	createdById: number | null;
}

export interface SupplierDetail {
	supplier: Supplier;
	totals: { currencyCode: string; currencySymbol: string; purchased: number; paid: number; balance: number; credit: number; cancelled: number }[];
	punctuality: { onTime: number; late: number; avgDaysLate: number };
	orders: OrderSummary[];
	products: { articleId: number; name: string; waCode: string | null; orders: number; quantity: number; received: number; lastUnitCost: number; lastCurrency: string; lastCurrencyCode: string | null; lastDate: string | null; firstUnitCost: number }[];
	payments: { id: number; kind: string; amount: number; currencySymbol: string; currencyCode: string | null; paidAt: string; reference: string | null; orders: { folio: string; orderId: number; amount: number }[]; author: string }[];
}

export interface PurchaseNeed {
	articleId: number;
	name: string;
	waCode: string | null;
	hasSizes: boolean;
	needed: number;
	paidFull: number;
	partial: number;
	onHand: number;
	inTransit: number;
	eta: string | null;
	toBuy: number;
}

export interface PurchaseCatalogs {
	countryCode: string;
	localCurrencyId: number;
	warehouses: { id: number; code: string; name: string; kind: string }[];
	currencies: { id: number; code: string; symbol: string }[];
	costTypes: { id: string; name: string }[];
	statuses: { id: number; name: string }[];
	suppliers: Supplier[];
	countries: { id: string; name: string }[];
}

/** Compras: órdenes (purchaseOrders), aprobación con pago (purchaseApprovals) y recepción (purchaseReceipts). */
export class PurchaseService {
	private form(data: Record<string, any>, files: File[] = [], key = "files"): FormData {
		const body = new FormData();
		Object.entries(data).forEach(([name, value]) => {
			if (value === undefined || value === null) return;
			body.append(name, typeof value === "object" ? JSON.stringify(value) : String(value));
		});
		files.forEach((file) => body.append(key + "[]", file));
		return body;
	}

	catalogs = async (campusId: number): Promise<AxiosResponse<PurchaseCatalogs>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_catalogs`);

	list = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders`, { params: { ...filters, page, perPage } });

	counts = async (campusId: number): Promise<AxiosResponse<Record<string, number>>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_counts`);

	needs = async (campusId: number): Promise<AxiosResponse<PurchaseNeed[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_needs`);

	get = async (campusId: number, orderId: number): Promise<AxiosResponse<OrderDetail>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/${orderId}`);

	save = async (campusId: number, orderId: number | null, data: Record<string, any>, files: File[]): Promise<AxiosResponse<any>> =>
		orderId === null
			? axiosInstance.post(`${BASE}${campusId}/purchaseOrders`, this.form(data, files))
			: axiosInstance.post(`${BASE}${campusId}/purchaseOrders/${orderId}/_update`, this.form(data, files));

	submit = async (campusId: number, orderId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseOrders/${orderId}/_submit`);

	cancel = async (campusId: number, orderId: number, reason: string, asApprover: boolean): Promise<AxiosResponse<any>> =>
		asApprover
			? axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_cancel`, { reason })
			: axiosInstance.delete(`${BASE}${campusId}/purchaseOrders/${orderId}`, { data: { reason } });

	addCost = async (campusId: number, orderId: number, data: Record<string, any>, files: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_costs`, this.form(data, files));

	voidCost = async (campusId: number, orderId: number, costId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_voidCost/${costId}`, { reason });

	approve = async (campusId: number, orderId: number, payment: Record<string, any>, installments: { dueDate: string; amount: number }[], files: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_approve`, this.form({ ...payment, installments }, files));

	reschedule = async (campusId: number, orderId: number, installments: { dueDate: string; amount: number }[], note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${BASE}${campusId}/purchaseApprovals/${orderId}/_schedule`, { installments, note });

	supplier = async (campusId: number, supplierId: number): Promise<AxiosResponse<SupplierDetail>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_supplier/${supplierId}`);

	openOrders = async (campusId: number, supplierId: number): Promise<AxiosResponse<OrderSummary[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseApprovals/_openOrders/${supplierId}`);

	supplierPayment = async (campusId: number, payment: Record<string, any>, allocations: { orderId: number; amount: number }[], files: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/_supplierPayment`, this.form({ ...payment, allocations }, files));

	cancelLines = async (campusId: number, orderId: number, lines: { lineId: number; quantity: number }[], reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_cancelLines`, { lines, reason });

	transferCredit = async (campusId: number, fromOrderId: number, toOrderId: number, amount: number, note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${fromOrderId}/_transferCredit`, { toOrderId, amount, note });

	payables = async (campusId: number): Promise<AxiosResponse<Payable[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseApprovals/_payables`);

	pendingLines = async (campusId: number): Promise<AxiosResponse<PendingLine[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_pendingLines`);

	reject = async (campusId: number, orderId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_reject`, { reason });

	addPayment = async (campusId: number, orderId: number, payment: Record<string, any>, files: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_payment`, this.form(payment, files));

	close = async (campusId: number, orderId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/${orderId}/_close`, { reason });

	receive = async (campusId: number, orderId: number, lines: { lineId: number; quantity: number; damaged: number }[], note: string, photos: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseReceipts/${orderId}/_receive`, this.form({ lines, note }, photos, "photos"));

	fileUrl = (campusId: number, orderId: number, fileId: number): string => `${BASE}${campusId}/purchaseOrders/${orderId}/_file/${fileId}`;

	file = async (campusId: number, orderId: number, fileId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/${orderId}/_file/${fileId}`, { responseType: "blob" });

	suppliers = async (campusId: number): Promise<AxiosResponse<Supplier[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_suppliers`);

	saveSupplier = async (campusId: number, supplierId: number | null, data: Record<string, any>): Promise<AxiosResponse<any>> =>
		supplierId === null
			? axiosInstance.post(`${BASE}${campusId}/purchaseOrders/_suppliers`, data)
			: axiosInstance.put(`${BASE}${campusId}/purchaseOrders/_suppliers/${supplierId}`, data);
}
